import "@tento-ui/components/round";
import "@tento-ui/components/input/form";

import { html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { DateTime, Utc } from "@tento-chrono";
import { Button } from "@tento-ui/components/button/button";
import { Divider } from "@tento-ui/components/divider";
import { Form } from "@tento-ui/components/input/form";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { LonaModalHeader } from "@tento-ui/ui/modal-header-styles";
import { LonaModalWebComponent } from "@tento-ui/ui/modal-component";
import { Round } from "@tento-ui/components/round";
import { TextInput } from "@tento-ui/components/input/text-input";
import { LonaApi } from "../../lib/api";
import { Router } from "@tento-ui/router";
import { Tracing } from "@tento-core/tracing";
import { Json, RowKey, RowNetworkKey } from "@tento-lona/sheets";
import { SchedulingLinks } from "@tento-lona";

@component({
  name: "lona-booking-confirmation",
})
export class BookingConfirmation extends LonaModalWebComponent {
  private $bg: Round = this.hydrate<Round>("bg").bind(Round.Props.MODAL);
  private $form: Form = this.hydrate("form");
  private startTime: Option<DateTime<Utc>> = null;
  private username: string = "";
  private slug: string = "";
  private pending: boolean = false;
  private resolve: Option<() => void> = null;

  constructor() {
    super();
    GESTURE_MANAGER.addPointerEvent(this.$("done-button"), {
      onClick: () => this.close(),
    });
  }

  static async show(props: BookingConfirmation.Props): Promise<void> {
    return new Promise((resolve) => {
      const $modal = BookingConfirmation.make();
      $modal.bind(props, resolve);
      $modal.reveal();
    });
  }

  bind(props: BookingConfirmation.Props, resolve: () => void): this {
    this.resolve = resolve;
    this.startTime = props.start;
    this.username = props.username;
    this.slug = props.slug;
    this.pending = false;

    this.$("info").textContent = new Intl.DateTimeFormat([], {
      dateStyle: "full",
      timeStyle: "short",
      timeZone: props.tzName,
    }).format(new Date(props.start.mse));

    this.$("title").textContent = "Confirm your booking";
    this.$("form").style.display = "";
    this.$("done").style.display = "none";
    this.renderForm();
    return this;
  }

  private renderForm(): void {
    this.$form.bind({
      inputs: [
        {
          name: "name",
          type: "text",
          label: "Name",
          placeholder: "Your name",
          required: true,
          autocomplete: "name",
        },
        {
          name: "email",
          type: "email",
          label: "Email",
          placeholder: "you@example.com",
          required: true,
          autocomplete: "email",
        },
      ],
      cancel: {
        label: "Cancel",
        onClick: () => this.close(),
      },
      submit: {
        label: "Book",
        onClick: ($inputs) => {
          const $name = $inputs.get("name");
          const $email = $inputs.get("email");
          const name = $name instanceof TextInput ? $name.getValue().trim() : "";
          const email = $email instanceof TextInput
            ? $email.getValue().trim()
            : "";
          void this.submitBooking(name, email);
        },
      },
    });
  }

  private async submitBooking(name: string, email: string): Promise<void> {
    const datetime = this.startTime;
    if (!datetime || !name || !email || this.pending) return;

    this.pending = true;
    const rowKey = SchedulingLinks.bookingAvailabilityRowKey(
      this.username,
      this.slug,
    );
    Tracing.log("public-booking-submit-start", {
      username: this.username,
      slug: this.slug,
      rowKey,
      startTime: datetime.toString(),
    });
    const result = await LonaApi.postResult("/api/rows/{rowKey}/cells", {
      path: { rowKey: RowNetworkKey.forRead(RowNetworkKey.fromWire(rowKey)) },
      body: {
        id: null,
        date: null,
        endDate: null,
        data: Json.wire({ startTime: datetime, bookerName: name, bookerEmail: email }),
      },
    });

    if (!result.isOk()) {
      this.pending = false;
      Tracing.log("public-booking-submit-failed", {
        username: this.username,
        slug: this.slug,
        rowKey,
        startTime: datetime.toString(),
        error: JSON.stringify(result.err()?.inner),
      });
      return;
    }

    this.pending = false;
    const bookingId = result.ok()?.cells?.[0]?.id ?? null;
    Tracing.log("public-booking-submit-success", {
      username: this.username,
      slug: this.slug,
      rowKey,
      bookingId,
      startTime: datetime.toString(),
    });

    // Hand off to the durable, refresh-surviving receipt view: the same booking
    // route with `?confirmed={bookingId}`. The base route serves on hard GET, so
    // the receipt survives refresh; the booking UUID is the capability the
    // server reads back. The inline #done pane is only a fallback for the
    // (defensive) no-id case.
    if (bookingId) {
      Router.navigateTo(
        `${SchedulingLinks.bookingPath(this.username, this.slug)}?confirmed=${bookingId}`,
      );
      this.close();
      return;
    }

    this.$("title").textContent = "Booking confirmed";
    this.$("form").style.display = "none";
    this.$("done").style.display = "flex";
  }

  private close(): void {
    this.resolve?.();
    this.resolve = null;
    this.dismiss();
  }

  onModalDismiss(): void {
    this.resolve?.();
    this.resolve = null;
  }

  static $styles: Style.Compat[] = [
    LonaModalWebComponent.$style,
    LonaModalHeader.$style,
    Divider.$hr,
    Button.$style,
    css`
      :host {
        --overlay-border-radius: 8px;
        --std-modal-max-width: 460px;
      }

      #root {
        position: relative;
      }

      hr {
        margin-block: 0;
      }

      #body {
        gap: 8px;
      }

      #done {
        display: none;
        gap: 16px;
        padding: 20px;
      }

      #done-copy {
        gap: 4px;
      }

      #done-text {
        --font-size: var(--h6-size);
        color: var(--green);
      }

      #done-sub {
        color: var(--text-sec);
      }

      #done-footer {
        flex-direction: row-reverse;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-col id="root">
      <std-round id="bg" floating></std-round>
      <std-col class="lona-modal-header">
        <h1 id="title">Confirm your booking</h1>
        <p id="info"></p>
      </std-col>
      <hr />
      <std-col id="body">
        <std-form id="form"></std-form>
        <std-col id="done">
          <std-col id="done-copy">
            <p id="done-text">You're all set.</p>
            <p id="done-sub" class="small">We've recorded your booking for the time above.</p>
          </std-col>
          <std-row id="done-footer">
            <button id="done-button" primary>Done</button>
          </std-row>
        </std-col>
      </std-col>
    </std-col>
  `;
}

export namespace BookingConfirmation {
  export type Props = {
    start: DateTime<Utc>;
    tzName: string;
    username: string;
    slug: string;
  };
}
